import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "./services/api";
import { useUi } from "./stores/ui";
import type { SessionUser } from "./types";
import { can } from "./types";
import { AppShell } from "./layouts/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { RosterPage } from "./pages/RosterPage";
import { ProfilePage } from "./pages/ProfilePage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { RecruitsPage } from "./pages/RecruitsPage";
import { LeaderboardsPage } from "./pages/LeaderboardsPage";
import { AdminDashboardPage, AuditPage, SettingsPage } from "./pages/AdminPages";
import { Skeleton } from "./components/ui";
import { Toasts } from "./components/feedback";
import type { Permission } from "@hrhub/shared";

function Guard({ children, permission }: { children: ReactNode; permission?: Permission }) {
  const user = useUi((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (permission && !can(user, permission)) {
    return <p className="p-8 text-zinc-400">You do not have permission to access this section.</p>;
  }
  return children;
}

export default function App() {
  const location = useLocation();
  const setUser = useUi((s) => s.setUser);
  const user = useUi((s) => s.user);
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const data = await api<{ user: SessionUser | null }>("/api/auth/me");
      setUser(data.user);
      return data.user;
    },
  });

  if (me.isLoading) {
    return (
      <>
        <Toasts />
        <div className="p-8"><Skeleton className="h-32" /></div>
      </>
    );
  }

  return (
    <>
      <Toasts />
      <Routes location={location}>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route
        element={
          <Guard>
            <AppShell />
          </Guard>
        }
      >
        <Route path="/" element={<RosterPage />} />
        <Route path="/roster" element={<Navigate to="/" replace />} />
        <Route path="/roster/:discordId" element={<ProfilePage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/recruits" element={<RecruitsPage />} />
        <Route path="/teams" element={<Navigate to="/recruits" replace />} />
        <Route path="/leaderboards" element={<LeaderboardsPage />} />
        <Route path="/archive" element={<Navigate to="/" replace />} />
        <Route path="/admin" element={<Guard permission="VIEW_ANALYTICS"><AdminDashboardPage /></Guard>} />
        <Route path="/admin/managers" element={<Navigate to="/" replace />} />
        <Route path="/admin/audit" element={<Guard permission="VIEW_AUDIT_LOGS"><AuditPage /></Guard>} />
        <Route path="/admin/settings" element={<Guard permission="MANAGE_SETTINGS"><SettingsPage /></Guard>} />
        <Route path="/history" element={<Navigate to="/" replace />} />
        <Route path="/notifications" element={<Navigate to="/" replace />} />
        <Route path="/heatmap" element={<Navigate to="/" replace />} />
      </Route>
      <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
      </Routes>
    </>
  );
}
